import { ApprovalOption, RecurringIntervalType } from "../AnnualWheel.types.ts";
import { useTranslation } from "@localization/useTranslation";
import { createElement, useEffect, useRef, useState } from "react";
import { ContractBasicInfo, RelativeDateConfigModel, RelativeDateReference } from "../RelativeDate.types.ts";
import { ActivityPaneProps } from "./ActivityPane.types";
import { AnnualWheelPriority, COMPLIANCE_AREA_CONTRACT_MANAGEMENT_ID } from "../annualWheelDialog/AnnualWheelDialog.types.ts";
import { isNullOrWhitespace } from "@common/stringOperations";
import { Trans } from "react-i18next";
import { post, put } from "@common/api/apiShared";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { AnnualWheelActivityViewModel } from "../AnnualWheelActivity.types.ts";
import { RelativeTemplateDateConfigModel } from "../../masterData/annualWheelActivityTemplate/customTemplateAddEditDialog/CustomTemplateAddEditDialog.types.ts";
import { usePanes } from "../../main/panes/usePanes";
import { usePane, useRegisterPaneDiscard } from "../../main/panes/paneLayout/usePane";
import { ActivityFormModel, ActivityFormValues } from "../activityForm/ActivityFormModel";
import {
    ActivityScheduleModel,
    ActivityScheduleValues,
    defaultActivityInterval,
    defaultActivityNotificationPeriodInDays,
} from "../activityForm/ActivityScheduleModel";

import { useDotLegalMutation, useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { useUrlProvider } from "../../useUrlProvider.ts";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider.tsx";
import { useLocation } from "react-router";

// The whole form, so a page refresh brings back everything typed. JSON turns the models into their values
// and the dates into strings, which the model constructors turn back into dates.
type AnnualWheelActivityDraft = Omit<ActivityFormValues, "schedule"> & { schedule: ActivityScheduleValues };

function getAnnualWheelActivityDraftKey(
    contractId: string | undefined,
    annualWheelStandardTemplateId: string | undefined,
    annualWheelActivityId: string | undefined
) {
    return `annualWheelActivity_${contractId}_${annualWheelStandardTemplateId}_${annualWheelActivityId}`;
}

function getAnnualWheelActivityDraft(
    contractId: string | undefined,
    annualWheelStandardTemplateId: string | undefined,
    annualWheelActivityId: string | undefined
): ActivityFormModel | undefined {
    const item = sessionStorage.getItem(getAnnualWheelActivityDraftKey(contractId, annualWheelStandardTemplateId, annualWheelActivityId));
    if (!item) {
        return undefined;
    }

    const draft: AnnualWheelActivityDraft = JSON.parse(item);
    return ActivityFormModel.create({ ...draft, schedule: ActivityScheduleModel.create(draft.schedule) });
}

function setAnnualWheelActivityDraft(
    contractId: string | undefined,
    annualWheelStandardTemplateId: string | undefined,
    annualWheelActivityId: string | undefined,
    model: ActivityFormModel
) {
    sessionStorage.setItem(getAnnualWheelActivityDraftKey(contractId, annualWheelStandardTemplateId, annualWheelActivityId), JSON.stringify(model));
}

function clearAnnualWheelActivityDraft(
    contractId: string | undefined,
    annualWheelStandardTemplateId: string | undefined,
    annualWheelActivityId: string | undefined
) {
    sessionStorage.removeItem(getAnnualWheelActivityDraftKey(contractId, annualWheelStandardTemplateId, annualWheelActivityId));
}

export function useActivityPane(props: ActivityPaneProps) {
    const { translateString } = useTranslation();
    const snackbar = useDotLegalSnackbar();
    const { refetchPageBehindPanes } = usePanes();
    const { close, requestClose, setHasUnsavedChanges } = usePane();

    const { annualWheelActivityId, template, mode } = props;
    const givenContractId = props.contractId;
    const startDate = props.startDate ?? new Date();
    const isReadOnly = mode === "read";
    const { getTaskManagementUrl, getTaskManagementUrlForTask } = useUrlProvider();
    const { pathname } = useLocation();
    const { permissions } = useUserContext();

    // Bumped on every rejected save so pressing Save again scrolls to the offender again, even when it is
    // the same one as last time.
    const [validationAttempt, setValidationAttempt] = useState(0);
    const [showFormError, setShowFormError] = useState<boolean>(false);
    const annualWheelUrl = "/annualWheelActivity/";
    const getAnnualWheelActivityUrl = `${annualWheelUrl + annualWheelActivityId}`;
    const { isLoading, data } = useDotLegalQuery<AnnualWheelActivityViewModel>(getAnnualWheelActivityUrl, {
        enabled: !isNullOrWhitespace(annualWheelActivityId),
    });

    const contractId = givenContractId ?? data?.contractId;
    const contractInfoUrl = `/contracts/${contractId}/basic-info`;
    const { data: contractInfo } = useDotLegalQuery<ContractBasicInfo>(contractInfoUrl, {
        enabled: !!contractId,
    });

    const annualWheelTemplateDraftIdKey = template?.standardTemplateId ?? template?.customTemplateId;
    // Read mode shares its draft key with the edit flows it is opened alongside, so without this it would
    // show whatever was last typed into the activity and abandoned, as if it were the saved activity.
    // Read once: the draft is rewritten on every change, and only the one the pane opened with is restored.
    const [annualWheelActivityDraft] = useState(() =>
        isReadOnly ? undefined : getAnnualWheelActivityDraft(givenContractId, annualWheelTemplateDraftIdKey, annualWheelActivityId)
    );

    // The draft outlives the pane, so confirming the discard has to reach it: without this the panel
    // promises the edits are gone and then hands them back on the next open. Registered on the pane
    // rather than done on unmount, because the pane also unmounts when it closes without asking, such as
    // after a save, and the draft must only go when the user has chosen to throw it away.
    useRegisterPaneDiscard(() => clearAnnualWheelActivityDraft(givenContractId, annualWheelTemplateDraftIdKey, annualWheelActivityId));

    // A restored draft is edits nobody has saved yet, so closing the pane has to ask before throwing them away.
    useEffect(() => {
        if (annualWheelActivityDraft) {
            setHasUnsavedChanges(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Every new activity without a template shares one draft key, so a draft abandoned in another month would
    // otherwise open on that month instead of the one just clicked.
    function withClickedStartDate(draft: ActivityFormModel) {
        const isNewActivity = isNullOrWhitespace(annualWheelActivityId);
        if (!isNewActivity || !props.startDate || draft.schedule.useRelativeStartDate) {
            return draft;
        }

        return draft.withUpdates({ schedule: draft.schedule.withUpdates({ startDate: props.startDate }) });
    }

    const [model, setModel] = useState(
        () =>
            (annualWheelActivityDraft && withClickedStartDate(annualWheelActivityDraft)) ??
            ActivityFormModel.create({
                name: template?.name ?? null,
                description: template?.description ?? null,
                subTasks: template?.subTasks.map((name) => ({ name, relations: [] })) ?? [],
                customId: template?.customId,
                priority: template?.priority ?? AnnualWheelPriority.Moderate,
                areas: givenContractId ? [COMPLIANCE_AREA_CONTRACT_MANAGEMENT_ID] : (template?.complianceAreas ?? []),
                frameWork: template?.framework,
                tags: template?.tags ?? [],
                generalType: template?.generalType,
                contractId: givenContractId,
                isProcessingActivityValidation: !!template?.isProcessingActivityValidation,
                customTemplateId: template?.customTemplateId,
                standardTemplateId: template?.standardTemplateId,
                schedule: ActivityScheduleModel.create({
                    startDate,
                    notificationPeriodInDays: template?.notificationPeriodInDays ?? defaultActivityNotificationPeriodInDays,
                    interval:
                        isNullOrWhitespace(template?.customTemplateId) && isNullOrWhitespace(template?.standardTemplateId)
                            ? defaultActivityInterval
                            : template?.interval,
                    recurrenceIntervalType: template?.recurringIntervalType ?? RecurringIntervalType.Month,
                }),
            })
    );

    useEffect(() => {
        if (contractInfo && annualWheelActivityDraft) {
            setModel((previous) => previous.withUpdates({ schedule: previous.schedule.withUpdates({ contractInfo }) }));
        } else if (contractInfo) {
            const relativeStartDateConfig = getTemplateRelativeDateConfig(template?.startDateRelativeConfig, contractInfo);
            const relativeEndDateConfig = getTemplateRelativeDateConfig(template?.endDateRelativeConfig, contractInfo);
            const hasRelativeDateFromTemplateButCantDisplay =
                (!!template?.startDateRelativeConfig && !relativeStartDateConfig) || (!!template?.endDateRelativeConfig && !relativeEndDateConfig);
            const isNewActivity = isNullOrWhitespace(annualWheelActivityId);

            setModel((previous) =>
                previous.withUpdates({
                    schedule: previous.schedule.withUpdates({
                        contractInfo,
                        startDateRelativeConfig: relativeStartDateConfig,
                        useRelativeStartDate: !!relativeStartDateConfig,
                        endDateRelativeConfig: relativeEndDateConfig,
                        useRelativeEndDate: !!relativeEndDateConfig,
                        displayEndDate: !!template?.endDateRelativeConfig,
                        hasRelativeDateFromTemplateButCantDisplay,
                    }),
                    businessAreas: isNewActivity ? resolveDefaultBusinessAreas(template?.businessAreas, contractInfo.businessAreaIds) : previous.businessAreas,
                })
            );
        }
        // eslint-disable-next-line
    }, [contractInfo]);

    // The saved activity only fills the form when there is no draft, because the draft already holds it with the edits on top.
    useEffect(() => {
        if (data && !annualWheelActivityDraft) {
            setModel((previous) =>
                previous.withUpdates({
                    name: data.name,
                    description: data.description,
                    subTasks: data.subTasks.map((name) => ({ name, relations: [] })),
                    relationsFromDescription: [],
                    relations: data.relations,
                    customId: data.customId,
                    priority: data.priority,
                    documentationRequired: data.documentationRequired,
                    responsible: data.responsible ?? undefined,
                    assignees: data.assignees ?? [],
                    approval: data.approval ?? ApprovalOption.ApprovalNotRequired,
                    groupCompanies: data.groupCompanies ?? [],
                    generalType: data.generalType,
                    areas: data.areas ?? [],
                    frameWork: undefined,
                    businessAreas: data.businessAreas ?? [],
                    tags: data.tags,
                    isProcessingActivityValidation: data.isProcessingActivityValidation || previous.isProcessingActivityValidation,
                    contractId: data.contractId,
                    contractDocumentId: data.contractDocumentId,
                    section: data.section,
                    clause: data.clause,
                    schedule: ActivityScheduleModel.create({
                        startDate: data.startDate,
                        notificationPeriodInDays: data.notificationPeriodInDays,
                        interval: data.interval,
                        recurrenceIntervalType: data.recurringIntervalType,
                        endDate: data.endDate,
                        displayEndDate: data.endDate !== null,
                        startDateRelativeConfig: data.startDateRelativeConfig ?? undefined,
                        endDateRelativeConfig: data.endDateRelativeConfig ?? undefined,
                        useRelativeStartDate: data.startDateRelativeConfig !== null,
                        useRelativeEndDate: data.endDateRelativeConfig !== null,
                        contractInfo,
                    }),
                })
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contractInfo, data]);

    const isEditActivity = data !== undefined;

    const getSaveButtonText = () => {
        return isEditActivity ? translateString("save") : translateString("add");
    };

    // Only changes the user makes count as unsaved, not the effects above that fill the model from the
    // server. The draft keeps what is typed, so it survives the pane being closed and reopened.
    const hasUserChanges = useRef(false);
    function onModelChange(updates: Partial<ActivityFormValues>) {
        markAsChangedByUser();
        setModel((current) => current.withUpdates(updates));
    }

    function onScheduleChange(updates: Partial<ActivityScheduleValues>) {
        markAsChangedByUser();
        setModel((current) => current.withUpdates({ schedule: current.schedule.withUpdates(updates) }));
    }

    function markAsChangedByUser() {
        hasUserChanges.current = true;
        setHasUnsavedChanges(true);
    }

    // Written after the render rather than in the change handlers, which only have the changed fields and not the model they produce.
    useEffect(() => {
        if (hasUserChanges.current) {
            setAnnualWheelActivityDraft(givenContractId, annualWheelTemplateDraftIdKey, annualWheelActivityId, model);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [model]);

    const onSaveClick = () => {
        /* Assigning a local variable, because the setShowFormError will not change the validation before we reach the if/else */
        const firstInvalidSection = model.getFirstInvalidSection();
        setShowFormError(firstInvalidSection !== undefined);
        setValidationAttempt((previous) => previous + 1);

        if (firstInvalidSection !== undefined) {
            return;
        }

        submitForm();
    };

    function submitForm() {
        if (isEditActivity) {
            updateActivity();
        } else {
            createActivity();
        }
    }

    const editMutation = useDotLegalMutation(update);

    function update(id: string) {
        return put<{}>(`${annualWheelUrl}${id}/`, model.toSaveModel(annualWheelActivityId));
    }
    const updateActivity = () => {
        editMutation.mutateAsync(annualWheelActivityId!, {
            onSuccess: () => {
                clearAnnualWheelActivityDraft(givenContractId, annualWheelTemplateDraftIdKey, annualWheelActivityId);
                snackbar.show(createElement(Trans, { i18nKey: "annualWheelActivityUpdated", values: { name: model.name } }));
                refetchPageBehindPanes();
                close();
            },
        });
    };

    const createActivity = async () => {
        await createMutation.mutateAsync();
    };

    function save() {
        return post<{}>(annualWheelUrl, model.toSaveModel(annualWheelActivityId));
    }

    const createMutation = useDotLegalMutation(save, {
        onSuccess: () => {
            clearAnnualWheelActivityDraft(givenContractId, annualWheelTemplateDraftIdKey, annualWheelActivityId);
            refetchPageBehindPanes();
            close();
            snackbar.show(createElement(Trans, { i18nKey: "annualWheelActivityAdded", values: { name: model.name } }));
        },
    });

    // The activity has tasks only once it has been created, and the task management view is where they are
    // followed, so the link is offered only for an existing activity and only to someone who may read tasks.
    // Opened from task management itself the link leads nowhere new, so it is left out there.
    const isOnTaskManagement = pathname.startsWith(getTaskManagementUrl());
    const taskManagementUrl =
        annualWheelActivityId && permissions.taskPermissions.read && !isOnTaskManagement
            ? getTaskManagementUrlForTask(undefined, annualWheelActivityId)
            : undefined;

    return {
        isLoading,
        isReadOnly,
        getSaveButtonText,
        firstInvalidSection: showFormError ? model.getFirstInvalidSection() : undefined,
        validationAttempt,
        onSaveClick,
        onCancelClick: requestClose,
        model,
        onModelChange,
        onScheduleChange,
        showFormError,
        showTrialWarning: template?.showTrialWarning ?? false,
        showLegalEntitySelector: !model.contractId,
        templateRecurringInterval: template?.interval,
        templateRecurringIntervalType: template?.recurringIntervalType,
        annualWheelActivityId,
        inProgress: editMutation.isLoading || createMutation.isLoading,
        contractInfo,
        taskManagementUrl,
    };
}

// A template's relative date is only kept when the contract has the date it counts from.
function getTemplateRelativeDateConfig(
    config: RelativeTemplateDateConfigModel | undefined,
    contractInfo: ContractBasicInfo
): RelativeDateConfigModel | undefined {
    const hasReferenceDate =
        (config?.relativeTo === RelativeDateReference.ContractStart && !!contractInfo.startDate) ||
        (config?.relativeTo === RelativeDateReference.ContractEnd && !!contractInfo.endDate);

    return config && hasReferenceDate ? { value: config.value!, unit: config.unit!, relativeTo: config.relativeTo!, direction: config.direction! } : undefined;
}

// An obligation template's default business areas are authoritative when it defines any. Only when it
// defines none do we fall back to inheriting the contract's, which is the pre-template behaviour.
function resolveDefaultBusinessAreas(templateBusinessAreas: Array<string> | undefined, contractBusinessAreaIds: Array<string>): Array<string> {
    return templateBusinessAreas && templateBusinessAreas.length > 0 ? templateBusinessAreas : contractBusinessAreaIds;
}
